import React, { useState } from "react";
import PropTypes from "prop-types";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  CircularProgress,
  Stack,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
  LinearProgress,
  Paper,
} from "@mui/material";
import {
  PaymentOutlined,
  SecurityOutlined,
  CheckCircleOutline,
  ErrorOutline,
} from "@mui/icons-material";

const PaypalBtns = ({
  amount,
  onPaymentSuccess,
  currency,
  buyerEmail,
  clientId,
  description,
  base,
  eventId,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const createOrder = (data, actions) => {
    setProcessingPayment(true);
    return actions.order.create({
      purchase_units: [
        {
          description: description,
          amount: {
            currency_code: currency,
            value: amount,
          },
        },
      ],
      application_context: {
        shipping_preference: "NO_SHIPPING",
        brand_name: "Event Registration",
        locale: "en-US",
        user_action: "PAY_NOW",
        // Disable save account option for test event
        ...(eventId === "7493635113" && {
          landing_page: "BILLING",
          user_action: "PAY_NOW",
        }),
      },
      payer: {
        email_address: buyerEmail,
      },
    });
  };

  const onApprove = (data, actions) => {
    setLoading(true);
    setProcessingPayment(true);
    actions.order
      .capture()
      .then((details) => {
        console.log("PayPal payment completed:", details);
        console.log("Order status:", details?.status);
        console.log(
          "Capture status:",
          details?.purchase_units?.[0]?.payments?.captures?.[0]?.status
        );

        onPaymentSuccess(details, actions);
        setLoading(false);
        setProcessingPayment(false);
      })
      .catch((error) => {
        console.error("PayPal capture error:", error);
        setErrorMessage("Payment capture failed. Please try again.");
        setLoading(false);
        setProcessingPayment(false);
      });
  };

  const onError = (data, actions) => {
    console.log(data, actions, "Error");
    setErrorMessage("An error occurred with your payment. Please try again.");
    setLoading(false);
    setProcessingPayment(false);
  };

  const onCancel = (data, actions) => {
    console.log("Payment cancelled by user");
    setProcessingPayment(false);
    // Don't show error for user cancellation
  };

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <PaymentOutlined sx={{ color: "#0070ba", fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: "#2c3e50", fontWeight: 600 }}>
              Secure Payment with PayPal
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Complete your registration securely with PayPal
          </Typography>

          {/* Payment Amount */}
          <Chip
            label={`${currency} ${amount}`}
            sx={{
              backgroundColor: "#0070ba",
              color: "white",
              fontWeight: "bold",
              fontSize: "1.1rem",
              py: 2,
              px: 1,
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Security Notice */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: "#e8f5e8", borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SecurityOutlined sx={{ color: "#4caf50", fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: "#2e7d32" }}>
              Your payment is secured by PayPal's advanced encryption
            </Typography>
          </Stack>
        </Box>

        {/* Loading State */}
        {(loading || processingPayment) && (
          <Box sx={{ mb: 3 }}>
            <Alert
              severity="info"
              icon={<CircularProgress size={20} />}
              sx={{
                backgroundColor: "#e3f2fd",
                "& .MuiAlert-icon": {
                  alignItems: "center",
                },
              }}
            >
              <Typography variant="body2">
                {processingPayment
                  ? "Processing your payment..."
                  : "Completing registration..."}
              </Typography>
            </Alert>
            <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
          </Box>
        )}

        {/* Error Message */}
        {errorMessage && (
          <Alert
            severity="error"
            icon={<ErrorOutline />}
            sx={{ mb: 3 }}
            onClose={() => setErrorMessage("")}
          >
            {errorMessage}
          </Alert>
        )}

        {/* PayPal Buttons Container */}
        <Box
          sx={{
            background: "white",
            borderRadius: 2,
            p: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          {!clientId ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress sx={{ color: "#0070ba" }} />
              <Typography
                variant="body2"
                sx={{ mt: 2, color: "text.secondary" }}
              >
                Loading PayPal...
              </Typography>
            </Box>
          ) : (
            <PayPalScriptProvider
              options={{
                "client-id": clientId,
                currency: currency,
                intent: "capture",
                "disable-funding":
                  eventId === "7493635113"
                    ? "credit,card,paylater"
                    : "credit,card",
                "enable-funding": "venmo",
              }}
            >
              <PayPalButtons
                style={{
                  layout: "vertical",
                  color: "blue",
                  shape: "rect",
                  label: "paypal",
                  height: 45,
                  tagline: false,
                }}
                createOrder={createOrder}
                onApprove={onApprove}
                onError={onError}
                onCancel={onCancel}
                disabled={loading || processingPayment}
              />
            </PayPalScriptProvider>
          )}
        </Box>

        {/* Footer Information */}
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ mb: 1 }}
          >
            <CheckCircleOutline sx={{ color: "#4caf50", fontSize: 16 }} />
            <Typography variant="caption" color="text.secondary">
              Instant confirmation upon successful payment
            </Typography>
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {eventId === "7493635113"
              ? "Test mode: Account creation disabled for demo purposes"
              : "You can pay with PayPal balance, bank account, or credit/debit card"}
          </Typography>
        </Box>
      </CardContent>
    </Paper>
  );
};

PaypalBtns.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onPaymentSuccess: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  buyerEmail: PropTypes.string.isRequired,
  clientId: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  base: PropTypes.string,
  eventId: PropTypes.string,
};

export default PaypalBtns;
